import React, { useRef, useEffect, useCallback, useState } from 'react';

interface GridProps {
  grid: number[][];
  getCellColor: (value: number) => string;
  gridScale: number;
}

const Grid: React.FC<GridProps> = ({ grid, getCellColor, gridScale }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [renderMode, setRenderMode] = useState<'pixels' | 'blocks' | 'dots'>('pixels');
  const [zoom, setZoom] = useState(1.0);

  // Debug: Log when Grid component receives new props
  useEffect(() => {
    console.log('🔷 Grid component: Props changed', {
      gridLength: grid.length,
      gridWidth: grid[0]?.length || 0,
      aliveCells: grid.flat().filter(v => v > 0).length,
      gridScale
    });
  }, [grid, gridScale]);

  // Use the getCellColor prop for proper color mode support (Mod8 or Full256)
  const getColor = useCallback((value: number): string => {
    if (value === 0) return '#000000';
    // Use the passed getCellColor which supports both Mod8 and Full256 modes
    return getCellColor(value);
  }, [getCellColor]);

  // Draw grid on canvas
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || grid.length === 0) {
      console.log('🎨 Grid.draw: Skipping - canvas or grid not ready', { canvas: !!canvas, gridLength: grid.length });
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.log('🎨 Grid.draw: No 2d context');
      return;
    }

    const gridSize = grid.length;
    const aliveCells = grid.flat().filter(v => v > 0).length;
    console.log(`🎨 Grid.draw: Drawing ${gridSize}x${gridSize} grid with ${aliveCells} alive cells`);

    const cellSize = Math.max(2, Math.floor(600 / gridSize));
    const displaySize = Math.min(600, gridSize * cellSize * zoom * gridScale);

    canvas.width = displaySize;
    canvas.height = displaySize;

    // Clear canvas with dark background
    ctx.fillStyle = '#0a0a1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate visible area
    const effectiveZoom = zoom * gridScale;
    const visibleCells = Math.floor(displaySize / (cellSize * effectiveZoom));
    const startCell = Math.max(0, Math.floor((gridSize - visibleCells) / 2));
    const endCell = Math.min(gridSize, startCell + visibleCells);

    if (renderMode === 'pixels') {
      // Fast pixel rendering
      for (let r = startCell; r < endCell; r++) {
        for (let c = startCell; c < endCell; c++) {
          const value = grid[r]?.[c] ?? 0;
          if (value > 0) {
            ctx.fillStyle = getColor(value);
            const x = (c - startCell) * cellSize * effectiveZoom;
            const y = (r - startCell) * cellSize * effectiveZoom;
            const size = Math.max(1, cellSize * effectiveZoom);
            ctx.fillRect(x, y, size, size);
          }
        }
      }
    } else if (renderMode === 'blocks') {
      // Blocks with grid lines
      for (let r = startCell; r < endCell; r++) {
        for (let c = startCell; c < endCell; c++) {
          const value = grid[r]?.[c] ?? 0;
          const x = (c - startCell) * cellSize * effectiveZoom;
          const y = (r - startCell) * cellSize * effectiveZoom;
          const size = Math.max(1, cellSize * effectiveZoom);

          ctx.fillStyle = getColor(value);
          ctx.fillRect(x, y, size, size);

          // Grid lines
          ctx.strokeStyle = 'rgba(0, 255, 136, 0.1)';
          ctx.lineWidth = 0.5;
          ctx.strokeRect(x, y, size, size);
        }
      }
    } else if (renderMode === 'dots') {
      // Dots for large grids
      for (let r = startCell; r < endCell; r++) {
        for (let c = startCell; c < endCell; c++) {
          const value = grid[r]?.[c] ?? 0;
          if (value > 0) {
            ctx.fillStyle = getColor(value);
            const x = (c - startCell) * cellSize * effectiveZoom + cellSize * effectiveZoom / 2;
            const y = (r - startCell) * cellSize * effectiveZoom + cellSize * effectiveZoom / 2;
            const radius = Math.max(1, cellSize * effectiveZoom / 3);
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, 2 * Math.PI);
            ctx.fill();
          }
        }
      }
    }

    // Draw border
    ctx.strokeStyle = '#00ff88';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, canvas.width, canvas.height);
  }, [grid, gridScale, zoom, renderMode, getColor]);

  // Redraw when grid changes
  useEffect(() => {
    draw();
  }, [draw]);

  // Auto-select render mode based on grid size
  useEffect(() => {
    if (grid.length >= 200) {
      setRenderMode('dots');
    } else if (grid.length >= 100) {
      setRenderMode('pixels');
    } else {
      setRenderMode('blocks');
    }
  }, [grid.length]);

  return (
    <div className="grid-container">
      <div className="canvas-controls">
        <label>
          Render: 
          <select 
            value={renderMode} 
            onChange={(e) => setRenderMode(e.target.value as 'pixels' | 'blocks' | 'dots')}
          >
            <option value="pixels">Pixels (Fast)</option>
            <option value="blocks">Blocks (Grid)</option>
            <option value="dots">Dots (Large)</option>
          </select>
        </label>
        <label>
          Zoom: 
          <input 
            type="range" 
            min="0.5" 
            max="2" 
            step="0.1" 
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
          />
          <span>{zoom.toFixed(1)}x</span>
        </label>
      </div>
      <div className="canvas-wrapper">
        <canvas 
          ref={canvasRef} 
          className="grid-canvas"
        />
      </div>
      <div className="grid-stats">
        <span>Grid: {grid.length}×{grid.length}</span>
        <span>Cells: {grid.length * grid.length}</span>
        <span>Alive: {grid.flat().filter(v => v > 0).length}</span>
      </div>
    </div>
  );
};

export default Grid;
